/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button/Button';
import Header from '@/components/UI/Header';
import APITable from '@/components/UI/Table/Reusable/APITable/APITable';
import type { UseAPITableProps, UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { TanStackColumns } from '@/components/UI/Table/TableComponent.model';
import type { StudyCode, StudyCodeApiId } from '@/model/StudyCode.model';
import { ApiService } from '@/support/ApiService';
import { web as webRoute } from '@/support/route';
import { RouteService } from '@/support/RouteService';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { generateStudyCodeColumns } from './StudyCode.utils';

const StudyCodeList = () => {
  const [columns, setColumns] = useState<TanStackColumns<StudyCode>>([]);
  const dispatch = useDispatch();

  const handleStudyCodeDelete = async (studyCodeApiIds: Array<StudyCodeApiId>) => {
    try {
      await ApiService.call({
        endpoint: 'DELETE /api/v1/team/study-codes',
        body: { study_code_api_ids: studyCodeApiIds },
      });
      toast.success('Successfully deleted!');
    } catch (error) {
      toast.error('There was a problem deleting study codes, please try again later.');
    }
  };

  useEffect(() => {
    setColumns(generateStudyCodeColumns());
  }, []);

  const bulkActions = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { setUpdating, refetchTableData },
    },
  }: UseTableProps<StudyCode>) => {
    return [
      {
        name: 'Delete',
        key: 'delete_study_codes',
        className: 'red',
        action: () => {
          const studyCodeApiIds = selectedFlatRows.map(({ original: { api_id } }) => api_id);
          dispatch({
            type: 'OPEN_MODAL',
            modal: 'CONFIRM_DELETE_STUDY_CODES',
            props: {
              onClick: () => {
                setUpdating(true);
                handleStudyCodeDelete(studyCodeApiIds)
                  .then(() => refetchTableData())
                  .catch(() => setUpdating(false));
              },
              length: studyCodeApiIds.length,
            },
          });
        },
      },
    ];
  };

  const NoDataComponent: React.FC<UseAPITableProps<StudyCode>> = () => (
    <NoDataCard
      title="Study codes will appear here"
      text="Study codes listed here can be added by team admins"
      NoDataComponent={
        <Button className="mt3" url={webRoute('team.studies.study_codes.create')}>
          Create a study code
        </Button>
      }
    />
  );

  const AsideComponent: React.FC<UseTableProps<StudyCode>> = () => (
    <Button className="ml2" url={webRoute('team.studies.study_codes.create')}>
      Create a study code
    </Button>
  );

  return (
    <>
      <div>
        <Header mainHeaderText="Study codes" />
        <p className="f6 pb2">
          Set up study codes templates for use in your research.
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="dib ml1 link blue"
            href={RouteService.help('study_codes')}
          >
            Read more
          </a>
        </p>
      </div>
      <div className="mv3">
        <div className={`ui-card treatmentsGlossary`}>
          <div>
            <APITable
              NoDataComponent={NoDataComponent}
              AsideComponent={AsideComponent}
              bulkActions={bulkActions}
              apiInfo={{
                type: 'internalApi',
                route: RouteService.api({
                  endpoint: 'GET /api/v1/team/study-codes',
                  path: undefined,
                }).url,
              }}
              reduxTableName="studycodes"
              searchPlaceholderText="Search by study code title"
              columns={columns.filter((col) => col.header)}
              defaultSortBy={{ id: 'title', desc: false }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default StudyCodeList;
